import { DeleteOutlined, WarningOutlined } from '@ant-design/icons';
import { useApolloClient } from '@apollo/client';
import { Button, message, Popconfirm } from 'antd';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import * as permissionKind from '../../../../shared/permissions';
import { type UserGroupSpecsFragment } from '../../../api/fragments/UserGroupSpecs';
import {
    RemoveDealerOnUserGroupDocument,
    type RemoveDealerOnUserGroupMutation,
    type RemoveDealerOnUserGroupMutationVariables,
} from '../../../api/mutations/removeDealerOnUserGroup';
import { useAccount } from '../../../components/contexts/AccountContextManager';
import { useCompany } from '../../../components/contexts/CompanyContextManager';
import hasPermissions from '../../../utilities/hasPermissions';

export type UserGroupDealerListActionsProps = {
    userGroup: UserGroupSpecsFragment;
    dealer: UserGroupSpecsFragment['dealers'][number];
};

const userGroupDealerListActions = ({ userGroup, dealer }: UserGroupDealerListActionsProps) => {
    const { t } = useTranslation(['userGroupDetails']);
    const apolloClient = useApolloClient();
    const dealerId = dealer.id;
    const userGroupId = userGroup.id;
    const { permissions: accountPermissions } = useAccount();
    const company = useCompany();

    const hasPermission = company
        ? hasPermissions(company.permissions, [permissionKind.deleteUserGroup]) &&
          hasPermissions(accountPermissions, [permissionKind.deleteUserGroup])
        : hasPermissions(accountPermissions, [permissionKind.deleteUserGroup]);

    const onDelete = useCallback(async () => {
        message.loading({
            content: t('userGroupDetails:deleteDealer.messages.submitting'),
            key: 'primary',
            duration: 0,
        });

        await apolloClient
            .mutate<RemoveDealerOnUserGroupMutation, RemoveDealerOnUserGroupMutationVariables>({
                mutation: RemoveDealerOnUserGroupDocument,
                variables: { userGroupId, dealerId },
            })
            .finally(() => {
                message.destroy('primary');
            });

        message.success({
            content: t('userGroupDetails:deleteDealer.messages.success'),
            key: 'primary',
        });
    }, [t, apolloClient, dealerId, userGroupId]);

    if (!hasPermission) {
        return null;
    }

    return (
        <Popconfirm
            cancelText={t('userGroupDetails:deleteDealer.cancelText')}
            icon={<WarningOutlined style={{ color: 'red' }} />}
            okText={t('userGroupDetails:deleteDealer.okText')}
            onConfirm={onDelete}
            placement="left"
            title={t('userGroupDetails:deleteDealer.message')}
        >
            <Button icon={<DeleteOutlined />} type="link" />
        </Popconfirm>
    );
};

export default userGroupDealerListActions;
